import { useState, useEffect } from 'react';
import {
  Box,
  Grid,
  Paper,
  Typography,
  Button,
  Skeleton,
  alpha,
  Chip,
  useMediaQuery,
  useTheme,
  Stack,
  Grow,
  LinearProgress,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  Avatar,
  Divider,
  Card,
  CardHeader,
  CardContent,
} from '@mui/material';
import {
  TrendingUp as TrendingUpIcon,
  DirectionsCar as CarIcon,
  People as PeopleIcon,
  AttachMoney as MoneyIcon,
  Add as AddIcon,
  Security as SecurityIcon,
  Assessment as AssessmentIcon,
  Search as SearchIcon,
  PersonAdd as PersonAddIcon,
  Handshake as HandshakeIcon,
  SwapHoriz as TransferIcon,
  Campaign as CampaignIcon,
  Print as PrintIcon,
  Build as BuildIcon,
  Description as DocumentIcon,
  Timeline as TimelineIcon,
  TaskAlt as TaskIcon,
  CalendarToday as CalendarIcon,
  CarRental as RentalIcon,
  Warning as WarningIcon,
  PhotoCamera as PhotoIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { vehicleService } from '../services/vehicles.service';
import { customerService } from '../services/customers.service';
import { salesService } from '../services/sales.service';
import { ReportsService } from '../services/reports.service';
import { TasksService } from '../services/tasks.service';
import { useAuth } from '../hooks/useAuth';
import AnimatedBox from '../components/AnimatedBox';
import { transitions } from '../theme/animations';
import TextField from '@mui/material/TextField';
import Dialog from '@mui/material/Dialog';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import CircularProgress from '@mui/material/CircularProgress';
import Alert from '@mui/material/Alert';
import autograbService from '../services/autograb.service';
import PPSRCheck from '../components/PPSRCheck';
import AppleStyleDealFlowEnhanced from '../components/AppleStyleDealFlowEnhanced';
import AddCustomerModal from '../components/customers/AddCustomerModal';
import { formatDistanceToNow, startOfDay, endOfDay, isAfter, isBefore, format } from 'date-fns';

type RecentActivityItem = {
  id: string;
  title: string;
  created_at: string;
  status: string;
  priority?: string | null;
  due_date?: string | null;
};

interface StatCardProps {
  title: string;
  value: string | number;
  icon: React.ReactNode;
  trend?: number;
  loading?: boolean;
}

function StatCard({ title, value, icon, trend, loading }: StatCardProps) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  return (
    <Card
      sx={{
        p: { xs: 2.5, sm: 3 },
        border: '1px solid',
        borderColor: alpha(theme.palette.divider, 0.1),
        borderRadius: 2,
        boxShadow: '0 1px 3px rgba(0,0,0,0.04)',
        bgcolor: theme.palette.mode === 'dark'
          ? 'rgba(255,255,255,0.02)'
          : 'white',
        height: '100%',
        transition: 'all 0.2s ease',
        '&:hover': {
          transform: 'translateY(-2px)',
          boxShadow: '0 4px 12px rgba(0,0,0,0.08)',
        },
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between' }}>
        <Box sx={{ flex: 1 }}>
          <Typography
            variant="caption"
            sx={{
              color: 'text.secondary',
              fontWeight: 500,
              letterSpacing: 0.5,
              textTransform: 'uppercase',
              fontSize: '0.7rem',
              mb: 1,
              display: 'block'
            }}
          >
            {title}
          </Typography>
          {loading ? (
            <Skeleton width={100} height={32} />
          ) : (
            <Typography
              variant={isMobile ? "h5" : "h4"}
              sx={{ fontWeight: 600, mb: 1, color: 'text.primary' }}
            >
              {value}
            </Typography>
          )}
          {!loading && trend !== undefined && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <TrendingUpIcon
                sx={{
                  fontSize: 14,
                  color: trend >= 0 ? 'success.main' : 'error.main'
                }}
              />
              <Typography
                variant="caption"
                sx={{
                  color: trend >= 0 ? 'success.main' : 'error.main',
                  fontWeight: 600,
                }}
              >
                {trend >= 0 ? '+' : ''}{trend}%
              </Typography>
            </Box>
          )}
        </Box>
        <Avatar
          sx={{
            width: 44,
            height: 44,
            bgcolor: alpha(theme.palette.primary.main, 0.1),
            color: 'primary.main',
          }}
        >
          {icon}
        </Avatar>
      </Box>
    </Card>
  );
}

export default function ResponsiveDashboard() {
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isTablet = useMediaQuery(theme.breakpoints.down('md'));
  
  const { currentOrganization } = useAuth();
  const [loading, setLoading] = useState(true);
  
  // Quick Tools state
  const [quickToolsRego, setQuickToolsRego] = useState('');
  const [quickToolsState, setQuickToolsState] = useState('NSW');
  const [ppsrDialogOpen, setPpsrDialogOpen] = useState(false);
  const [valuationDialogOpen, setValuationDialogOpen] = useState(false);
  const [valuationLoading, setValuationLoading] = useState(false);
  const [valuationResult, setValuationResult] = useState<any>(null);
  const [quickToolsError, setQuickToolsError] = useState('');
  // dealFlowOpen state removed — sale creation now navigates to /sales/new
  const [addCustomerModalOpen, setAddCustomerModalOpen] = useState(false);
  const [regoExpiryModalOpen, setRegoExpiryModalOpen] = useState(false);
  const [inspectionExpiryModalOpen, setInspectionExpiryModalOpen] = useState(false);
  const [transfersModalOpen, setTransfersModalOpen] = useState(false);
  const [disposalModalOpen, setDisposalModalOpen] = useState(false);
  const [tasksModalOpen, setTasksModalOpen] = useState(false);
  const [photosModalOpen, setPhotosModalOpen] = useState(false);
  const [expiringRegoVehicles, setExpiringRegoVehicles] = useState<any[]>([]);
  const [expiringInspectionVehicles, setExpiringInspectionVehicles] = useState<any[]>([]);
  const [vehiclesMissingPhotos, setVehiclesMissingPhotos] = useState<any[]>([]);
  const [upcomingTasks, setUpcomingTasks] = useState<any[]>([]);
  const [pendingRegistrationTransfers, setPendingRegistrationTransfers] = useState<any[]>([]);
  const [pendingDisposalNotices, setPendingDisposalNotices] = useState<any[]>([]);

  const [stats, setStats] = useState({
    totalVehicles: 0,
    availableVehicles: 0,
    totalCustomers: 0,
    activeLeads: 0,
    totalSales: 0,
    totalRevenue: 0,
    monthlyRevenue: 0,
    openDeals: 0,
    averageDaysInStock: 0,
    inventoryTurnoverRate: 0,
    inventoryByStatus: { available: 0, on_offer: 0, reserved: 0, sold: 0, in_service: 0, other: 0 },
  });
  const [conversionRate, setConversionRate] = useState(0);
  const [conversionFunnel, setConversionFunnel] = useState<Array<{ stage: string; count: number; percentage: number }>>([]);
  const [leadsByStatus, setLeadsByStatus] = useState<Array<{ status: string; count: number }>>([]);
  const [taskSummary, setTaskSummary] = useState({ open: 0, overdue: 0, dueToday: 0 });
  const [recentActivity, setRecentActivity] = useState<RecentActivityItem[]>([]);

  useEffect(() => {
    let mounted = true;
    
    const load = async () => {
      if (mounted && currentOrganization) {
        await loadDashboardData();
      }
    };
    
    load();
    
    return () => {
      mounted = false;
    };
  }, [currentOrganization?.id]);

  const loadDashboardData = async () => {
    if (!currentOrganization) return;

    setLoading(true);
    try {
      // Get date range for metrics (all time)
      const endDate = new Date();
      const startDate = new Date();
      startDate.setFullYear(startDate.getFullYear() - 10); // Last 10 years

      const [
        vehiclesResult,
        customersResult,
        salesResult,
        tasksResult,
        customerMetrics,
      ] = await Promise.all([
        vehicleService.getVehicles({}),
        customerService.getCustomers(),
        salesService.getDeals({}, 1, 1000), // Fetch more deals to get accurate data
        TasksService.getTasks(),
        ReportsService.getCustomerMetrics({ start: startDate, end: endDate }),
      ]);

      const vehicles = vehiclesResult.data || [];
      const customers = customersResult.data || [];
      const sales = salesResult.data?.data || [];
      const tasks = tasksResult.data || [];

      // Calculate monthly revenue (last 30 days)
      const thirtyDaysAgo = new Date();
      thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);

      // Include both 'completed' and 'won' as completed sales
      const completedSales = sales.filter(sale =>
        sale.stage === 'completed' || sale.status === 'completed' ||
        sale.stage === 'won' || sale.status === 'won'
      );
      const totalRevenue = completedSales.reduce((sum, sale) => sum + (sale.total_amount || 0), 0);

      const monthlySales = completedSales.filter(sale =>
        (sale.sale_date || sale.actual_close_date) &&
        new Date(sale.sale_date || sale.actual_close_date) >= thirtyDaysAgo
      );
      const monthlyRevenue = monthlySales.reduce((sum, sale) => sum + (sale.total_amount || 0), 0);
      const openDeals = sales.filter(sale => !['completed', 'lost', 'cancelled', 'won'].includes((sale.stage || sale.status || '').toLowerCase())).length;

      const activeLeadStatuses = ['new', 'contacted', 'test_drive', 'proposal'];
      const metricActiveLeads = customerMetrics?.leadsByStatus
        ?.filter(item => activeLeadStatuses.includes(item.status))
        .reduce((sum, item) => sum + item.count, 0) ?? 0;
      const derivedActiveLeads = customers.filter(c => activeLeadStatuses.includes(c.lead_status)).length;

      const now = new Date();
      const todayStart = startOfDay(now);
      const todayEnd = endOfDay(now);
      const openTasks = tasks.filter(task => !['completed', 'cancelled', 'done'].includes(task.status));
      const overdueTasks = openTasks.filter(task => task.due_date && isBefore(new Date(task.due_date), todayStart)).length;
      const dueTodayTasks = openTasks.filter(task => {
        if (!task.due_date) {
          return false;
        }
        const due = new Date(task.due_date);
        return (isAfter(due, todayStart) || due.getTime() === todayStart.getTime()) &&
          (isBefore(due, todayEnd) || due.getTime() === todayEnd.getTime());
      }).length;

      const recentTasks: RecentActivityItem[] = tasks
        .slice()
        .sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime())
        .slice(0, 4)
        .map(task => ({
          id: task.id,
          title: task.title,
          created_at: task.created_at,
          status: task.status,
          priority: task.priority,
          due_date: task.due_date,
        }));

      // Calculate average days in stock
      const vehiclesWithDates = vehicles.filter(v => v.created_at && v.status === 'available');
      const totalDaysInStock = vehiclesWithDates.reduce((sum, v) => {
        const addedDate = new Date(v.created_at);
        const daysInStock = Math.floor((now.getTime() - addedDate.getTime()) / (1000 * 60 * 60 * 24));
        return sum + daysInStock;
      }, 0);
      const averageDaysInStock = vehiclesWithDates.length > 0 ? Math.round(totalDaysInStock / vehiclesWithDates.length) : 0;

      // Calculate inventory turnover rate (annual)
      // Turnover = Vehicles sold in last 30 days / Current inventory * 12 (annualized)
      const vehiclesSoldLast30Days = vehicles.filter(v => {
        if (!v.sold_date) return false;
        const soldDate = new Date(v.sold_date);
        return soldDate >= thirtyDaysAgo && soldDate <= now;
      }).length;

      const currentInventory = vehicles.filter(v => v.status === 'available').length || 1;
      const monthlyTurnover = vehiclesSoldLast30Days / currentInventory;
      const inventoryTurnoverRate = parseFloat((monthlyTurnover * 12).toFixed(2)); // Annualized

      // Calculate inventory by status - USING ACTUAL SCHEMA VALUES
      const inventoryByStatus = {
        available: vehicles.filter(v => v.status === 'available').length,
        on_offer: vehicles.filter(v => v.status === 'on_offer').length,
        reserved: vehicles.filter(v => v.status === 'reserved').length,
        sold: vehicles.filter(v => v.status === 'sold').length,
        in_service: vehicles.filter(v => v.status === 'in_service').length,
        other: vehicles.filter(v => !['available', 'on_offer', 'reserved', 'sold', 'in_service'].includes(v.status)).length,
      };

      setStats({
        totalVehicles: vehicles.length,
        availableVehicles: vehicles.filter(v => v.status === 'available').length,
        totalCustomers: customers.length,
        activeLeads: metricActiveLeads || derivedActiveLeads,
        totalSales: completedSales.length,
        totalRevenue,
        monthlyRevenue,
        openDeals,
        averageDaysInStock,
        inventoryTurnoverRate,
        inventoryByStatus,
      });
      setConversionRate(customerMetrics?.leadConversionRate || 0);
      setConversionFunnel(customerMetrics?.conversionFunnel || []);
      setLeadsByStatus(customerMetrics?.leadsByStatus || []);
      setTaskSummary({ open: openTasks.length, overdue: overdueTasks, dueToday: dueTodayTasks });
      setRecentActivity(recentTasks);

      // Get expiring rego and inspections (within 30 days)
      const thirtyDaysFromNow = new Date();
      thirtyDaysFromNow.setDate(thirtyDaysFromNow.getDate() + 30);

      const expiringRego = vehicles.filter(v => {
        if (!v.registration_expiry) return false;
        const expiryDate = new Date(v.registration_expiry);
        return expiryDate <= thirtyDaysFromNow && expiryDate >= now;
      });

      const expiringInspections = vehicles.filter(v => {
        if (!v.inspection_expiry) return false;
        const expiryDate = new Date(v.inspection_expiry);
        return expiryDate <= thirtyDaysFromNow && expiryDate >= now;
      });

      setExpiringRegoVehicles(expiringRego);
      setExpiringInspectionVehicles(expiringInspections);

      // Get vehicles missing photos (no images or empty images array)
      const missingPhotos = vehicles.filter(v => {
        return !v.images || v.images.length === 0 || (Array.isArray(v.images) && v.images.every((img: string) => !img));
      });
      setVehiclesMissingPhotos(missingPhotos);

      // Get upcoming tasks (due today or within 7 days)
      const sevenDaysFromNow = new Date();
      sevenDaysFromNow.setDate(sevenDaysFromNow.getDate() + 7);

      const upcoming = openTasks
        .filter(task => {
          if (!task.due_date) return false;
          const dueDate = new Date(task.due_date);
          return dueDate <= sevenDaysFromNow && dueDate >= todayStart;
        })
        .sort((a, b) => new Date(a.due_date).getTime() - new Date(b.due_date).getTime());

      setUpcomingTasks(upcoming);

      // Get pending registration transfers (sold vehicles within last 14 days)
      const fourteenDaysAgo = new Date();
      fourteenDaysAgo.setDate(fourteenDaysAgo.getDate() - 14);

      const pendingTransfers = vehicles.filter(v => {
        if (!v.sold_date) return false;
        const soldDate = new Date(v.sold_date);
        // Vehicle sold within last 14 days
        return soldDate >= fourteenDaysAgo && soldDate <= now;
      });
      setPendingRegistrationTransfers(pendingTransfers);

      // Get pending disposal notices (completed sales that require notice)
      const pendingDisposals = sales.filter(sale => {
        // Must be a completed sale
        if (sale.stage !== 'completed' && sale.status !== 'completed') return false;
        // Must require notice of disposal (field is false or undefined)
        if (sale.does_not_require_notice_of_disposal === true) return false;
        // Sale must have a date
        if (!sale.sale_date) return false;

        const saleDate = new Date(sale.sale_date);
        // Within last 14 days (disposal notice window)
        return saleDate >= fourteenDaysAgo && saleDate <= now;
      });
      setPendingDisposalNotices(pendingDisposals);
    } catch (error) {
      console.error('Error loading dashboard data:', error);
      setConversionRate(0);
      setConversionFunnel([]);
      setLeadsByStatus([]);
      setTaskSummary({ open: 0, overdue: 0, dueToday: 0 });
      setRecentActivity([]);
      setExpiringRegoVehicles([]);
      setExpiringInspectionVehicles([]);
      setVehiclesMissingPhotos([]);
      setUpcomingTasks([]);
      setPendingRegistrationTransfers([]);
      setPendingDisposalNotices([]);
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-AU', {
      style: 'currency',
      currency: 'AUD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount);
  };

  const getDaysUntil = (dateString: string) => {
    const date = new Date(dateString);
    const now = new Date();
    const diff = date.getTime() - now.getTime();
    return Math.ceil(diff / (1000 * 60 * 60 * 24));
  };
  
  const handleQuickPPSR = () => {
    if (!quickToolsRego) {
      setQuickToolsError('Please enter a registration number');
      return;
    }
    setQuickToolsError('');
    setPpsrDialogOpen(true);
  };
  
  const handleQuickValuation = async () => {
    if (!quickToolsRego) {
      setQuickToolsError('Please enter a registration number');
      return;
    }
    
    setQuickToolsError('');
    setValuationLoading(true);
    setValuationResult(null);
    setValuationDialogOpen(true);
    
    try {
      const valuationData = await autograbService.getValuation({
        registration: quickToolsRego,
        state: quickToolsState,
        kilometres: 50000 // Default value
      });
      
      setValuationResult(valuationData.prediction);
    } catch (error: any) {
      console.error('Valuation failed:', error);
      setQuickToolsError(error.message || 'Failed to get valuation');
    } finally {
      setValuationLoading(false);
    }
  };

  return (
    <Box sx={{ width: '100%' }}>
      {/* Welcome Section */}
      <AnimatedBox animation="fadeInDown" sx={{ pt: { xs: 2, sm: 2, md: 3 }, mb: { xs: 3, md: 4 } }}>
        <Typography 
          variant="body2" 
          color="text.secondary" 
          sx={{ mb: 0.5, fontSize: { xs: '0.75rem', sm: '0.875rem' } }}
        >
          Welcome back
        </Typography>
        <Typography 
          variant={isMobile ? "h5" : "h4"} 
          sx={{ fontWeight: 600, mb: { xs: 2, sm: 3 } }}
        >
          {currentOrganization?.name || 'Dashboard'}
        </Typography>

        {/* Quick Actions - Clean Apple Style */}
        <Grid container spacing={2}>
          <Grid item xs={6} sm={4} md={2}>
            <Card
              sx={{
                bgcolor: 'primary.main',
                color: 'white',
                cursor: 'pointer',
                transition: 'all 0.2s ease',
                height: '100%',
                minHeight: 120,
                border: 'none',
                borderRadius: 2,
                '&:hover': {
                  bgcolor: 'primary.dark',
                  transform: 'translateY(-4px)',
                  boxShadow: '0 8px 20px rgba(0,0,0,0.12)',
                }
              }}
              onClick={() => navigate('/inventory')}
            >
              <CardContent sx={{
                p: 2.5,
                textAlign: 'center',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                height: '100%'
              }}>
                <CarIcon sx={{ fontSize: 36, mb: 1, opacity: 0.95 }} />
                <Typography variant="body1" sx={{ fontWeight: 600, fontSize: '0.9rem' }}>
                  Inventory
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={6} sm={4} md={2}>
            <Card
              sx={{
                bgcolor: 'primary.main',
                color: 'white',
                cursor: 'pointer',
                transition: 'all 0.2s ease',
                height: '100%',
                minHeight: 120,
                border: 'none',
                borderRadius: 2,
                '&:hover': {
                  bgcolor: 'primary.dark',
                  transform: 'translateY(-4px)',
                  boxShadow: '0 8px 20px rgba(0,0,0,0.12)',
                }
              }}
              onClick={() => navigate('/customers')}
            >
              <CardContent sx={{
                p: 2.5,
                textAlign: 'center',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                height: '100%'
              }}>
                <PeopleIcon sx={{ fontSize: 36, mb: 1, opacity: 0.95 }} />
                <Typography variant="body1" sx={{ fontWeight: 600, fontSize: '0.9rem' }}>
                  Customers
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={6} sm={4} md={2}>
            <Card
              sx={{
                bgcolor: 'primary.main',
                color: 'white',
                cursor: 'pointer',
                transition: 'all 0.2s ease',
                height: '100%',
                minHeight: 120,
                border: 'none',
                borderRadius: 2,
                '&:hover': {
                  bgcolor: 'primary.dark',
                  transform: 'translateY(-4px)',
                  boxShadow: '0 8px 20px rgba(0,0,0,0.12)',
                }
              }}
              onClick={() => navigate('/calendar')}
            >
              <CardContent sx={{
                p: 2.5,
                textAlign: 'center',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                height: '100%'
              }}>
                <CalendarIcon sx={{ fontSize: 36, mb: 1, opacity: 0.95 }} />
                <Typography variant="body1" sx={{ fontWeight: 600, fontSize: '0.9rem' }}>
                  Calendar
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={6} sm={4} md={2}>
            <Card
              sx={{
                bgcolor: 'primary.main',
                color: 'white',
                cursor: 'pointer',
                transition: 'all 0.2s ease',
                height: '100%',
                minHeight: 120,
                border: 'none',
                borderRadius: 2,
                '&:hover': {
                  bgcolor: 'primary.dark',
                  transform: 'translateY(-4px)',
                  boxShadow: '0 8px 20px rgba(0,0,0,0.12)',
                }
              }}
              onClick={() => navigate('/advertising')}
            >
              <CardContent sx={{
                p: 2.5,
                textAlign: 'center',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                height: '100%'
              }}>
                <PhotoIcon sx={{ fontSize: 36, mb: 1, opacity: 0.95 }} />
                <Typography variant="body1" sx={{ fontWeight: 600, fontSize: '0.9rem' }}>
                  Advertising
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={6} sm={4} md={2}>
            <Card
              sx={{
                bgcolor: 'primary.main',
                color: 'white',
                cursor: 'pointer',
                transition: 'all 0.2s ease',
                height: '100%',
                minHeight: 120,
                border: 'none',
                borderRadius: 2,
                '&:hover': {
                  bgcolor: 'primary.dark',
                  transform: 'translateY(-4px)',
                  boxShadow: '0 8px 20px rgba(0,0,0,0.12)',
                }
              }}
              onClick={() => navigate('/ppsr-reports')}
            >
              <CardContent sx={{
                p: 2.5,
                textAlign: 'center',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                height: '100%'
              }}>
                <SecurityIcon sx={{ fontSize: 36, mb: 1, opacity: 0.95 }} />
                <Typography variant="body1" sx={{ fontWeight: 600, fontSize: '0.9rem' }}>
                  PPSR
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={6} sm={4} md={2}>
            <Card
              sx={{
                bgcolor: 'primary.main',
                color: 'white',
                cursor: 'pointer',
                transition: 'all 0.2s ease',
                height: '100%',
                minHeight: 120,
                border: 'none',
                borderRadius: 2,
                '&:hover': {
                  bgcolor: 'primary.dark',
                  transform: 'translateY(-4px)',
                  boxShadow: '0 8px 20px rgba(0,0,0,0.12)',
                }
              }}
              onClick={() => navigate('/documents')}
            >
              <CardContent sx={{
                p: 2.5,
                textAlign: 'center',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                height: '100%'
              }}>
                <DocumentIcon sx={{ fontSize: 36, mb: 1, opacity: 0.95 }} />
                <Typography variant="body1" sx={{ fontWeight: 600, fontSize: '0.9rem' }}>
                  Forms
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </AnimatedBox>

      {/* Critical Alerts */}
      <AnimatedBox animation="fadeInUp" delay={50} triggerOnScroll>
        <Card sx={{
          p: { xs: 2.5, sm: 3 },
          mb: 3,
          border: '1px solid',
          borderColor: alpha(theme.palette.divider, 0.1),
          borderRadius: 2,
          boxShadow: '0 1px 3px rgba(0,0,0,0.04)',
          bgcolor: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.02)' : 'white'
        }}>
          <Typography variant={isMobile ? "body1" : "h6"} sx={{ mb: 2, fontWeight: 600 }}>
            Action Required
          </Typography>
          <Grid container spacing={2} sx={{ '& .MuiGrid-item': { display: 'flex' } }}>
            {/* Registration Transfers */}
            <Grid item xs={12} md={6}>
              <Card
                sx={{
                  p: 2,
                  border: '1px solid',
                  borderRadius: 2,
                  width: '100%',
                  borderColor: pendingRegistrationTransfers.length > 0 ? alpha(theme.palette.error.main, 0.3) : alpha(theme.palette.success.main, 0.3),
                  bgcolor: pendingRegistrationTransfers.length > 0 ? alpha(theme.palette.error.main, 0.02) : alpha(theme.palette.success.main, 0.02),
                  cursor: 'pointer',
                  transition: 'all 0.2s',
                  '&:hover': {
                    borderColor: pendingRegistrationTransfers.length > 0 ? 'error.dark' : 'success.dark',
                    bgcolor: pendingRegistrationTransfers.length > 0 ? alpha('#f44336', 0.1) : alpha('#4caf50', 0.1),
                    transform: 'translateY(-2px)',
                    boxShadow: 2,
                  }
                }}
                onClick={() => setTransfersModalOpen(true)}
              >
                <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5 }}>
                  <Avatar sx={{ bgcolor: pendingRegistrationTransfers.length > 0 ? 'error.main' : 'success.main', width: 36, height: 36 }}>
                    <TransferIcon sx={{ fontSize: 20 }} />
                  </Avatar>
                  <Box sx={{ flex: 1 }}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 0.5 }}>
                      Registration Transfers
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.875rem' }}>
                      {pendingRegistrationTransfers.length > 0
                        ? `${pendingRegistrationTransfers.length} pending within 14 days`
                        : 'All transfers complete'}
                    </Typography>
                  </Box>
                </Box>
              </Card>
            </Grid>

            {/* Notice of Disposal */}
            <Grid item xs={12} md={6}>
              <Card
                sx={{
                  p: 2,
                  border: '1px solid',
                  borderRadius: 2,
                  width: '100%',
                  borderColor: pendingDisposalNotices.length > 0 ? alpha(theme.palette.warning.main, 0.3) : alpha(theme.palette.success.main, 0.3),
                  bgcolor: pendingDisposalNotices.length > 0 ? alpha(theme.palette.warning.main, 0.02) : alpha(theme.palette.success.main, 0.02),
                  cursor: 'pointer',
                  transition: 'all 0.2s',
                  '&:hover': {
                    borderColor: pendingDisposalNotices.length > 0 ? 'warning.dark' : 'success.dark',
                    bgcolor: pendingDisposalNotices.length > 0 ? alpha('#ff9800', 0.1) : alpha('#4caf50', 0.1),
                    transform: 'translateY(-2px)',
                    boxShadow: 2,
                  }
                }}
                onClick={() => setDisposalModalOpen(true)}
              >
                <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5 }}>
                  <Avatar sx={{ bgcolor: pendingDisposalNotices.length > 0 ? 'warning.main' : 'success.main', width: 36, height: 36 }}>
                    <DocumentIcon sx={{ fontSize: 20 }} />
                  </Avatar>
                  <Box sx={{ flex: 1 }}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 0.5 }}>
                      Notice of Disposal
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.875rem' }}>
                      {pendingDisposalNotices.length > 0
                        ? `${pendingDisposalNotices.length} require lodgement`
                        : 'All notices lodged'}
                    </Typography>
                  </Box>
                </Box>
              </Card>
            </Grid>

            {/* Upcoming Tasks */}
            <Grid item xs={12} md={6}>
              <Card
                sx={{
                  p: 2,
                  border: '1px solid',
                  width: '100%',
                  borderColor: taskSummary.overdue > 0 ? 'error.main' : (upcomingTasks.length > 0 ? 'warning.main' : 'success.main'),
                  bgcolor: taskSummary.overdue > 0 ? alpha('#f44336', 0.05) : (upcomingTasks.length > 0 ? alpha('#ff9800', 0.05) : alpha('#4caf50', 0.05)),
                  cursor: 'pointer',
                  transition: 'all 0.2s',
                  '&:hover': {
                    borderColor: taskSummary.overdue > 0 ? 'error.dark' : (upcomingTasks.length > 0 ? 'warning.dark' : 'success.dark'),
                    bgcolor: taskSummary.overdue > 0 ? alpha('#f44336', 0.1) : (upcomingTasks.length > 0 ? alpha('#ff9800', 0.1) : alpha('#4caf50', 0.1)),
                    transform: 'translateY(-2px)',
                    boxShadow: 2,
                  }
                }}
                onClick={() => setTasksModalOpen(true)}
              >
                <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5 }}>
                  <Avatar sx={{ bgcolor: taskSummary.overdue > 0 ? 'error.main' : (upcomingTasks.length > 0 ? 'warning.main' : 'success.main'), width: 36, height: 36 }}>
                    <TaskIcon sx={{ fontSize: 20 }} />
                  </Avatar>
                  <Box sx={{ flex: 1 }}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 0.5 }}>
                      {taskSummary.overdue > 0 ? 'Overdue Tasks' : 'Upcoming Tasks'}
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.875rem' }}>
                      {taskSummary.overdue > 0
                        ? `${taskSummary.overdue} overdue, ${upcomingTasks.length} upcoming`
                        : upcomingTasks.length > 0
                        ? `${upcomingTasks.length} due this week`
                        : 'No tasks this week'}
                    </Typography>
                  </Box>
                </Box>
              </Card>
            </Grid>

            {/* Missing Photos */}
            <Grid item xs={12} md={6}>
              <Card
                sx={{
                  p: 2,
                  border: '1px solid',
                  borderRadius: 2,
                  width: '100%',
                  borderColor: vehiclesMissingPhotos.length > 0 ? alpha(theme.palette.info.main, 0.3) : alpha(theme.palette.success.main, 0.3),
                  bgcolor: vehiclesMissingPhotos.length > 0 ? alpha(theme.palette.info.main, 0.02) : alpha(theme.palette.success.main, 0.02),
                  cursor: 'pointer',
                  transition: 'all 0.2s',
                  '&:hover': {
                    borderColor: vehiclesMissingPhotos.length > 0 ? 'info.dark' : 'success.dark',
                    bgcolor: vehiclesMissingPhotos.length > 0 ? alpha('#2196f3', 0.1) : alpha('#4caf50', 0.1),
                    transform: 'translateY(-2px)',
                    boxShadow: 2,
                  }
                }}
                onClick={() => setPhotosModalOpen(true)}
              >
                <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5 }}>
                  <Avatar sx={{ bgcolor: vehiclesMissingPhotos.length > 0 ? 'info.main' : 'success.main', width: 36, height: 36 }}>
                    <PhotoIcon sx={{ fontSize: 20 }} />
                  </Avatar>
                  <Box sx={{ flex: 1 }}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 0.5 }}>
                      Vehicles Missing Photos
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.875rem' }}>
                      {vehiclesMissingPhotos.length > 0
                        ? `${vehiclesMissingPhotos.length} need photos added`
                        : 'All vehicles have photos'}
                    </Typography>
                  </Box>
                </Box>
              </Card>
            </Grid>

            {/* Rego Expiry */}
            <Grid item xs={12} md={6}>
              <Card
                sx={{
                  p: 2,
                  border: '1px solid',
                  borderRadius: 2,
                  width: '100%',
                  borderColor: expiringRegoVehicles.length > 0 ? alpha(theme.palette.error.main, 0.3) : alpha(theme.palette.success.main, 0.3),
                  bgcolor: expiringRegoVehicles.length > 0 ? alpha(theme.palette.error.main, 0.02) : alpha(theme.palette.success.main, 0.02),
                  cursor: 'pointer',
                  transition: 'all 0.2s',
                  '&:hover': {
                    borderColor: expiringRegoVehicles.length > 0 ? 'error.dark' : 'success.dark',
                    bgcolor: expiringRegoVehicles.length > 0 ? alpha('#f44336', 0.1) : alpha('#4caf50', 0.1),
                    transform: 'translateY(-2px)',
                    boxShadow: 2,
                  }
                }}
                onClick={() => setRegoExpiryModalOpen(true)}
              >
                <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5 }}>
                  <Avatar sx={{ bgcolor: expiringRegoVehicles.length > 0 ? 'error.main' : 'success.main', width: 36, height: 36 }}>
                    <WarningIcon sx={{ fontSize: 20 }} />
                  </Avatar>
                  <Box sx={{ flex: 1 }}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 0.5 }}>
                      Registration Expiry
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.875rem' }}>
                      {expiringRegoVehicles.length > 0
                        ? `${expiringRegoVehicles.length} expiring within 30 days`
                        : 'All registrations current'}
                    </Typography>
                  </Box>
                </Box>
              </Card>
            </Grid>

            {/* Inspection Expiry */}
            <Grid item xs={12} md={6}>
              <Card
                sx={{
                  p: 2,
                  border: '1px solid',
                  borderRadius: 2,
                  width: '100%',
                  borderColor: expiringInspectionVehicles.length > 0 ? alpha(theme.palette.warning.main, 0.3) : alpha(theme.palette.success.main, 0.3),
                  bgcolor: expiringInspectionVehicles.length > 0 ? alpha(theme.palette.warning.main, 0.02) : alpha(theme.palette.success.main, 0.02),
                  cursor: 'pointer',
                  transition: 'all 0.2s',
                  '&:hover': {
                    borderColor: expiringInspectionVehicles.length > 0 ? 'warning.dark' : 'success.dark',
                    bgcolor: expiringInspectionVehicles.length > 0 ? alpha('#ff9800', 0.1) : alpha('#4caf50', 0.1),
                    transform: 'translateY(-2px)',
                    boxShadow: 2,
                  }
                }}
                onClick={() => setInspectionExpiryModalOpen(true)}
              >
                <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5 }}>
                  <Avatar sx={{ bgcolor: expiringInspectionVehicles.length > 0 ? 'warning.main' : 'success.main', width: 36, height: 36 }}>
                    <AssessmentIcon sx={{ fontSize: 20 }} />
                  </Avatar>
                  <Box sx={{ flex: 1 }}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 0.5 }}>
                      Inspection Reports
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.875rem' }}>
                      {expiringInspectionVehicles.length > 0
                        ? `${expiringInspectionVehicles.length} expiring within 30 days`
                        : 'All inspections current'}
                    </Typography>
                  </Box>
                </Box>
              </Card>
            </Grid>
          </Grid>
        </Card>
      </AnimatedBox>

      {/* Today's Tasks */}
      <AnimatedBox animation="fadeInUp" delay={350} triggerOnScroll>
        <Paper sx={{
          p: { xs: 2, sm: 3 },
          mb: 3,
          border: '1px solid',
          borderColor: alpha(theme.palette.divider, 0.1),
          borderRadius: 2,
          boxShadow: '0 1px 3px rgba(0,0,0,0.04)',
          bgcolor: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.02)' : 'white',
        }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: { xs: 2, sm: 3 } }}>
            <Typography
              variant={isMobile ? "body1" : "h6"}
              sx={{ fontWeight: 600 }}
            >
              Today's Tasks
            </Typography>
            <Chip
              label={`${taskSummary.dueToday} due`}
              size="small"
              color={taskSummary.dueToday > 0 ? 'warning' : 'success'}
              sx={{ fontWeight: 600 }}
            />
          </Box>
          <Stack spacing={2}>
            {(() => {
              const todayStart = startOfDay(new Date());
              const todayEnd = endOfDay(new Date());
              const todaysTasks = upcomingTasks.filter(task => {
                if (!task.due_date) return false;
                const due = new Date(task.due_date);
                return (isAfter(due, todayStart) || due.getTime() === todayStart.getTime()) &&
                  (isBefore(due, todayEnd) || due.getTime() === todayEnd.getTime());
              });

              return todaysTasks.length === 0 ? (
                <Alert severity="success" sx={{ fontSize: '0.875rem' }}>
                  No tasks due today. Great job staying on top of things!
                </Alert>
              ) : (
                <Grid container spacing={2}>
                  {todaysTasks.slice(0, 6).map((task) => (
                    <Grid item xs={12} sm={6} md={4} key={task.id}>
                      <Box
                        sx={{
                          p: 2,
                          borderRadius: 2,
                          bgcolor: 'background.default',
                          border: '1px solid',
                          borderColor: alpha(theme.palette.divider, 0.5),
                          cursor: 'pointer',
                          transition: 'all 0.2s ease',
                          height: '100%',
                          boxShadow: '0 1px 2px rgba(0,0,0,0.02)',
                          '&:hover': {
                            borderColor: 'primary.main',
                            bgcolor: alpha(theme.palette.primary.main, 0.04),
                            transform: 'translateY(-1px)',
                            boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
                          }
                        }}
                        onClick={() => navigate('/calendar')}
                      >
                        <Typography
                          variant="body2"
                          sx={{
                            fontWeight: 500,
                            fontSize: '0.875rem',
                            mb: 0.5,
                            display: '-webkit-box',
                            WebkitLineClamp: 2,
                            WebkitBoxOrient: 'vertical',
                            overflow: 'hidden',
                          }}
                        >
                          {task.title}
                        </Typography>
                        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
                          {task.priority && (
                            <Chip
                              label={task.priority}
                              size="small"
                              color={
                                task.priority === 'high' ? 'error' :
                                task.priority === 'medium' ? 'warning' : 'default'
                              }
                              sx={{ height: 18, fontSize: '0.7rem' }}
                            />
                          )}
                          <Typography variant="caption" color="text.secondary">
                            {format(new Date(task.due_date), 'h:mm a')}
                          </Typography>
                        </Box>
                      </Box>
                    </Grid>
                  ))}
                </Grid>
              );
            })()}
            {taskSummary.dueToday > 6 && (
              <Button
                variant="outlined"
                size="small"
                onClick={() => navigate('/calendar')}
                fullWidth
              >
                View all {taskSummary.dueToday} tasks in Calendar
              </Button>
            )}
          </Stack>
        </Paper>
      </AnimatedBox>

      {/* Stats Grid */}
      <Grid container spacing={{ xs: 2, sm: 3 }} sx={{ mb: { xs: 3, md: 4 } }}>
        <Grid item xs={12} sm={6} lg={4}>
          <AnimatedBox animation="fadeInUp" delay={100} triggerOnScroll>
            <Card
              sx={{
                p: { xs: 2.5, sm: 3 },
                border: '1px solid',
                borderColor: alpha(theme.palette.divider, 0.1),
                borderRadius: 2,
                boxShadow: '0 1px 3px rgba(0,0,0,0.04)',
                bgcolor: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.02)' : 'white',
                height: '100%',
                transition: 'all 0.2s ease',
                '&:hover': {
                  transform: 'translateY(-2px)',
                  boxShadow: '0 4px 12px rgba(0,0,0,0.08)',
                },
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', mb: 2 }}>
                <Typography
                  variant="caption"
                  sx={{
                    color: 'text.secondary',
                    fontWeight: 500,
                    letterSpacing: 0.5,
                    textTransform: 'uppercase',
                    fontSize: '0.7rem',
                  }}
                >
                  Average Days in Stock
                </Typography>
                <Avatar
                  sx={{
                    width: 36,
                    height: 36,
                    bgcolor: alpha(theme.palette.info.main, 0.1),
                    color: 'info.main',
                  }}
                >
                  <TimelineIcon fontSize="small" />
                </Avatar>
              </Box>

              {loading ? (
                <Skeleton variant="rectangular" height={100} />
              ) : (
                <Box>
                  <Typography variant="h3" sx={{ fontWeight: 700, mb: 1, color: 'text.primary' }}>
                    {stats.averageDaysInStock}
                  </Typography>
                  <Typography variant="body2" sx={{ color: 'text.secondary', mb: 2 }}>
                    days
                  </Typography>

                  <Box sx={{ mt: 2 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.5 }}>
                      <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '0.7rem' }}>
                        Inventory Health
                      </Typography>
                      <Typography variant="caption" sx={{
                        fontWeight: 600,
                        fontSize: '0.7rem',
                        color: stats.averageDaysInStock <= 30 ? 'success.main' :
                               stats.averageDaysInStock <= 60 ? 'warning.main' : 'error.main'
                      }}>
                        {stats.averageDaysInStock <= 30 ? 'Excellent' :
                         stats.averageDaysInStock <= 60 ? 'Good' : 'Needs Attention'}
                      </Typography>
                    </Box>
                    <Box sx={{
                      height: 4,
                      bgcolor: alpha(theme.palette.grey[500], 0.1),
                      borderRadius: 999,
                      overflow: 'hidden'
                    }}>
                      <Box sx={{
                        height: '100%',
                        width: `${Math.min((stats.averageDaysInStock / 90) * 100, 100)}%`,
                        bgcolor: stats.averageDaysInStock <= 30 ? theme.palette.success.main :
                                 stats.averageDaysInStock <= 60 ? theme.palette.warning.main : theme.palette.error.main,
                        borderRadius: 999,
                        transition: 'all 0.3s ease'
                      }} />
                    </Box>
                    <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '0.65rem', mt: 0.5, display: 'block' }}>
                      Target: ≤ 30 days
                    </Typography>
                  </Box>
                </Box>
              )}
            </Card>
          </AnimatedBox>
        </Grid>
        <Grid item xs={12} sm={6} lg={4}>
          <AnimatedBox animation="fadeInUp" delay={200} triggerOnScroll>
            <Card
              sx={{
                p: { xs: 2.5, sm: 3 },
                border: '1px solid',
                borderColor: alpha(theme.palette.divider, 0.1),
                borderRadius: 2,
                boxShadow: '0 1px 3px rgba(0,0,0,0.04)',
                bgcolor: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.02)' : 'white',
                height: '100%',
                transition: 'all 0.2s ease',
                '&:hover': {
                  transform: 'translateY(-2px)',
                  boxShadow: '0 4px 12px rgba(0,0,0,0.08)',
                },
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', mb: 2 }}>
                <Typography
                  variant="caption"
                  sx={{
                    color: 'text.secondary',
                    fontWeight: 500,
                    letterSpacing: 0.5,
                    textTransform: 'uppercase',
                    fontSize: '0.7rem',
                  }}
                >
                  Inventory Turnover Rate
                </Typography>
                <Avatar
                  sx={{
                    width: 36,
                    height: 36,
                    bgcolor: alpha(theme.palette.success.main, 0.1),
                    color: 'success.main',
                  }}
                >
                  <TrendingUpIcon fontSize="small" />
                </Avatar>
              </Box>

              {loading ? (
                <Skeleton variant="rectangular" height={100} />
              ) : (
                <Box>
                  <Typography variant="h3" sx={{ fontWeight: 700, mb: 1, color: 'text.primary' }}>
                    {stats.inventoryTurnoverRate}
                  </Typography>
                  <Typography variant="body2" sx={{ color: 'text.secondary', mb: 2 }}>
                    times per year
                  </Typography>

                  <Box sx={{ mt: 2 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.5 }}>
                      <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '0.7rem' }}>
                        Performance
                      </Typography>
                      <Typography variant="caption" sx={{
                        fontWeight: 600,
                        fontSize: '0.7rem',
                        color: stats.inventoryTurnoverRate >= 8 ? 'success.main' :
                               stats.inventoryTurnoverRate >= 4 ? 'warning.main' : 'error.main'
                      }}>
                        {stats.inventoryTurnoverRate >= 8 ? 'Excellent' :
                         stats.inventoryTurnoverRate >= 4 ? 'Good' : 'Below Target'}
                      </Typography>
                    </Box>
                    <Box sx={{
                      height: 4,
                      bgcolor: alpha(theme.palette.grey[500], 0.1),
                      borderRadius: 999,
                      overflow: 'hidden'
                    }}>
                      <Box sx={{
                        height: '100%',
                        width: `${Math.min((stats.inventoryTurnoverRate / 12) * 100, 100)}%`,
                        bgcolor: stats.inventoryTurnoverRate >= 8 ? theme.palette.success.main :
                                 stats.inventoryTurnoverRate >= 4 ? theme.palette.warning.main : theme.palette.error.main,
                        borderRadius: 999,
                        transition: 'all 0.3s ease'
                      }} />
                    </Box>
                    <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '0.65rem', mt: 0.5, display: 'block' }}>
                      Target: ≥ 8x/year
                    </Typography>
                  </Box>
                </Box>
              )}
            </Card>
          </AnimatedBox>
        </Grid>
        <Grid item xs={12} sm={12} lg={4}>
          <AnimatedBox animation="fadeInUp" delay={300} triggerOnScroll>
            <Card
              sx={{
                p: { xs: 2.5, sm: 3 },
                border: '1px solid',
                borderColor: alpha(theme.palette.divider, 0.1),
                borderRadius: 2,
                boxShadow: '0 1px 3px rgba(0,0,0,0.04)',
                bgcolor: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.02)' : 'white',
                height: '100%',
                transition: 'all 0.2s ease',
                '&:hover': {
                  transform: 'translateY(-2px)',
                  boxShadow: '0 4px 12px rgba(0,0,0,0.08)',
                },
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', mb: 2 }}>
                <Typography
                  variant="caption"
                  sx={{
                    color: 'text.secondary',
                    fontWeight: 500,
                    letterSpacing: 0.5,
                    textTransform: 'uppercase',
                    fontSize: '0.7rem',
                  }}
                >
                  Inventory Status
                </Typography>
                <Avatar
                  sx={{
                    width: 36,
                    height: 36,
                    bgcolor: alpha(theme.palette.primary.main, 0.1),
                    color: 'primary.main',
                  }}
                >
                  <CarIcon fontSize="small" />
                </Avatar>
              </Box>

              {loading ? (
                <Skeleton variant="rectangular" height={150} />
              ) : (
                <Stack spacing={1.5}>
                  {/* Available */}
                  <Box>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.5 }}>
                      <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '0.75rem' }}>
                        Available
                      </Typography>
                      <Typography variant="caption" sx={{ fontWeight: 600, fontSize: '0.75rem' }}>
                        {stats.inventoryByStatus.available}
                      </Typography>
                    </Box>
                    <Box sx={{
                      height: 4,
                      bgcolor: alpha(theme.palette.success.main, 0.1),
                      borderRadius: 999,
                      overflow: 'hidden'
                    }}>
                      <Box sx={{
                        height: '100%',
                        width: `${stats.totalVehicles > 0 ? (stats.inventoryByStatus.available / stats.totalVehicles * 100) : 0}%`,
                        bgcolor: theme.palette.success.main,
                        borderRadius: 999,
                        transition: 'width 0.3s ease'
                      }} />
                    </Box>
                  </Box>

                  {/* On Offer */}
                  <Box>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.5 }}>
                      <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '0.75rem' }}>
                        On Offer
                      </Typography>
                      <Typography variant="caption" sx={{ fontWeight: 600, fontSize: '0.75rem' }}>
                        {stats.inventoryByStatus.on_offer}
                      </Typography>
                    </Box>
                    <Box sx={{
                      height: 4,
                      bgcolor: alpha(theme.palette.primary.main, 0.1),
                      borderRadius: 999,
                      overflow: 'hidden'
                    }}>
                      <Box sx={{
                        height: '100%',
                        width: `${stats.totalVehicles > 0 ? (stats.inventoryByStatus.on_offer / stats.totalVehicles * 100) : 0}%`,
                        bgcolor: theme.palette.primary.main,
                        borderRadius: 999,
                        transition: 'width 0.3s ease'
                      }} />
                    </Box>
                  </Box>

                  {/* Reserved */}
                  <Box>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.5 }}>
                      <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '0.75rem' }}>
                        Reserved
                      </Typography>
                      <Typography variant="caption" sx={{ fontWeight: 600, fontSize: '0.75rem' }}>
                        {stats.inventoryByStatus.reserved}
                      </Typography>
                    </Box>
                    <Box sx={{
                      height: 4,
                      bgcolor: alpha(theme.palette.warning.main, 0.1),
                      borderRadius: 999,
                      overflow: 'hidden'
                    }}>
                      <Box sx={{
                        height: '100%',
                        width: `${stats.totalVehicles > 0 ? (stats.inventoryByStatus.reserved / stats.totalVehicles * 100) : 0}%`,
                        bgcolor: theme.palette.warning.main,
                        borderRadius: 999,
                        transition: 'width 0.3s ease'
                      }} />
                    </Box>
                  </Box>

                  {/* Sold */}
                  <Box>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.5 }}>
                      <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '0.75rem' }}>
                        Sold
                      </Typography>
                      <Typography variant="caption" sx={{ fontWeight: 600, fontSize: '0.75rem' }}>
                        {stats.inventoryByStatus.sold}
                      </Typography>
                    </Box>
                    <Box sx={{
                      height: 4,
                      bgcolor: alpha(theme.palette.grey[500], 0.1),
                      borderRadius: 999,
                      overflow: 'hidden'
                    }}>
                      <Box sx={{
                        height: '100%',
                        width: `${stats.totalVehicles > 0 ? (stats.inventoryByStatus.sold / stats.totalVehicles * 100) : 0}%`,
                        bgcolor: theme.palette.grey[500],
                        borderRadius: 999,
                        transition: 'width 0.3s ease'
                      }} />
                    </Box>
                  </Box>
                </Stack>
              )}
            </Card>
          </AnimatedBox>
        </Grid>
      </Grid>

      {/* Pipeline Snapshot & Sales Analytics */}
      <AnimatedBox animation="fadeInUp" delay={350} triggerOnScroll>
        <Grid container spacing={{ xs: 2, sm: 3 }} sx={{ mb: 3 }}>
          {/* Lead Pipeline Snapshot */}
          <Grid item xs={12} lg={6}>
            <Paper sx={{
              p: { xs: 2, sm: 3 },
              border: '1px solid',
              borderColor: alpha(theme.palette.divider, 0.1),
              borderRadius: 2,
              boxShadow: '0 1px 3px rgba(0,0,0,0.04)',
              bgcolor: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.02)' : 'white',
              height: '100%',
            }}>
              <Typography
                variant={isMobile ? "body1" : "h6"}
                sx={{ mb: { xs: 2, sm: 3 }, fontWeight: 600 }}
              >
                Lead Pipeline
              </Typography>
              <Stack spacing={2}>
                {loading ? (
                  <Skeleton variant="rectangular" height={200} />
                ) : (() => {
                  // Calculate actual lead pipeline using ACTUAL schema statuses
                  const leadStages = [
                    {
                      status: 'new_lead',
                      label: 'New Lead',
                      color: theme.palette.info.main,
                      dbStatuses: ['new', 'contacted'] // Group new + contacted
                    },
                    {
                      status: 'test_drive',
                      label: 'Test Drive',
                      color: theme.palette.warning.main,
                      dbStatuses: ['test_drive']
                    },
                    {
                      status: 'proposal',
                      label: 'Proposal',
                      color: theme.palette.primary.main,
                      dbStatuses: ['proposal'] // JUST proposal - actual schema value
                    },
                    {
                      status: 'won',
                      label: 'Won',
                      color: theme.palette.success.main,
                      dbStatuses: ['won']
                    },
                    {
                      status: 'lost',
                      label: 'Lost',
                      color: theme.palette.error.main,
                      dbStatuses: ['lost', 'inactive'] // Group lost + inactive
                    },
                  ];

                  // Get actual customer counts by summing grouped DB statuses
                  const leadData = leadStages.map(stage => {
                    // Sum counts from all DB statuses that map to this stage
                    const count = stage.dbStatuses.reduce((sum, dbStatus) => {
                      return sum + (leadsByStatus.find(item => item.status === dbStatus)?.count || 0);
                    }, 0);
                    return { ...stage, count };
                  });

                  const totalLeads = leadData.reduce((sum, stage) => sum + stage.count, 0);

                  return totalLeads === 0 ? (
                    <Typography variant="body2" color="text.secondary">
                      No active leads in pipeline. Add customers to see your pipeline.
                    </Typography>
                  ) : (
                    leadData.map(stage => {
                      const percentage = totalLeads > 0 ? Math.round((stage.count / totalLeads) * 100) : 0;
                      return (
                        <Box key={stage.status}>
                          <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 0.5 }}>
                            <Typography variant="body2" sx={{ fontWeight: 500 }}>
                              {stage.label}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              {stage.count} ({percentage}%)
                            </Typography>
                          </Stack>
                          <Box sx={{
                            height: 4,
                            bgcolor: alpha(stage.color, 0.08),
                            borderRadius: 999,
                            overflow: 'hidden'
                          }}>
                            <Box sx={{
                              height: '100%',
                              width: `${percentage}%`,
                              bgcolor: stage.color,
                              borderRadius: 999,
                              transition: 'width 0.3s ease'
                            }} />
                          </Box>
                        </Box>
                      );
                    })
                  );
                })()}
              </Stack>
            </Paper>
          </Grid>

          {/* Sales Analytics Snapshot */}
          <Grid item xs={12} lg={6}>
            <Paper sx={{
              p: { xs: 2, sm: 3 },
              border: '1px solid',
              borderColor: alpha(theme.palette.divider, 0.1),
              borderRadius: 2,
              boxShadow: '0 1px 3px rgba(0,0,0,0.04)',
              bgcolor: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.02)' : 'white',
              height: '100%',
            }}>
              <Typography
                variant={isMobile ? "body1" : "h6"}
                sx={{ mb: { xs: 2, sm: 3 }, fontWeight: 600 }}
              >
                Sales Analytics
              </Typography>
              <Stack spacing={3}>
                <Box>
                  <Stack direction="row" justifyContent="space-between" alignItems="center">
                    <Box>
                      <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.75rem', mb: 0.5 }}>
                        Completed Sales
                      </Typography>
                      <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.65rem' }}>
                        All time
                      </Typography>
                    </Box>
                    <Typography variant="h4" sx={{ fontWeight: 700, color: 'text.primary' }}>
                      {stats.totalSales}
                    </Typography>
                  </Stack>
                </Box>
                <Box>
                  <Stack direction="row" justifyContent="space-between" alignItems="center">
                    <Box>
                      <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.75rem', mb: 0.5 }}>
                        Active Deals
                      </Typography>
                      <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.65rem' }}>
                        In progress
                      </Typography>
                    </Box>
                    <Typography variant="h4" sx={{ fontWeight: 700, color: 'text.primary' }}>
                      {stats.openDeals}
                    </Typography>
                  </Stack>
                </Box>
                <Box>
                  <Stack direction="row" justifyContent="space-between" alignItems="center">
                    <Box>
                      <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.75rem', mb: 0.5 }}>
                        Total Revenue
                      </Typography>
                      <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.65rem' }}>
                        All completed sales
                      </Typography>
                    </Box>
                    <Typography variant="h4" sx={{ fontWeight: 700, color: 'success.main' }}>
                      {formatCurrency(stats.totalRevenue || 0)}
                    </Typography>
                  </Stack>
                </Box>
                <Box>
                  <Stack direction="row" justifyContent="space-between" alignItems="center">
                    <Box>
                      <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.75rem', mb: 0.5 }}>
                        Last 30 Days Revenue
                      </Typography>
                      <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.65rem' }}>
                        Recent sales
                      </Typography>
                    </Box>
                    <Typography variant="h4" sx={{ fontWeight: 700, color: 'text.primary' }}>
                      {formatCurrency(stats.monthlyRevenue)}
                    </Typography>
                  </Stack>
                </Box>
              </Stack>
            </Paper>
          </Grid>
        </Grid>
      </AnimatedBox>

      {/* Recent Activity */}
      <Paper sx={{
        p: { xs: 2, sm: 3 },
        mb: 3,
        border: '1px solid',
        borderColor: alpha(theme.palette.divider, 0.1),
        borderRadius: 2,
        boxShadow: '0 1px 3px rgba(0,0,0,0.04)',
        bgcolor: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.02)' : 'white',
      }}>
        <Typography
          variant={isMobile ? "body1" : "h6"}
          sx={{ mb: { xs: 2, sm: 3 }, fontWeight: 600 }}
        >
          Recent Activity
        </Typography>
        <Stack spacing={2}>
          {recentActivity.length === 0 ? (
            <Alert severity="info">
              No activity yet. Complete a task or update a deal to see it here.
            </Alert>
          ) : (
            recentActivity.map((activity) => (
              <Box
                key={activity.id}
                sx={{
                  display: 'flex',
                  alignItems: { xs: 'flex-start', sm: 'center' },
                  justifyContent: 'space-between',
                  flexDirection: { xs: 'column', sm: 'row' },
                  gap: { xs: 1, sm: 2 },
                  p: { xs: 2, sm: 2 },
                  borderRadius: 2,
                  bgcolor: 'background.default',
                  border: '1px solid',
                  borderColor: alpha(theme.palette.divider, 0.5),
                  boxShadow: '0 1px 2px rgba(0,0,0,0.02)',
                  transition: 'all 0.2s ease',
                  '&:hover': {
                    borderColor: alpha(theme.palette.primary.main, 0.3),
                    boxShadow: '0 2px 6px rgba(0,0,0,0.04)',
                  }
                }}
              >
                <Box sx={{ flex: 1 }}>
                  <Typography
                    variant="body2"
                    sx={{
                      fontWeight: 500,
                      fontSize: { xs: '0.8125rem', sm: '0.875rem' }
                    }}
                  >
                    {activity.title}
                  </Typography>
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{ fontSize: { xs: '0.75rem', sm: '0.75rem' } }}
                  >
                    {formatDistanceToNow(new Date(activity.created_at), { addSuffix: true })}
                    {activity.due_date ? ` • Due ${format(new Date(activity.due_date), 'dd MMM')}` : ''}
                    {activity.priority ? ` • Priority: ${activity.priority}` : ''}
                  </Typography>
                </Box>
                <Chip
                  label={activity.status.replace(/_/g, ' ')}
                  size="small"
                  icon={<TaskIcon sx={{ fontSize: 16 }} />}
                  sx={{
                    height: { xs: 20, sm: 24 },
                    fontSize: { xs: '0.7rem', sm: '0.75rem' },
                    textTransform: 'capitalize'
                  }}
                />
              </Box>
            ))
          )}
        </Stack>
      </Paper>

      {/* PPSR Dialog */}
      <Dialog
        open={ppsrDialogOpen}
        onClose={() => setPpsrDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>PPSR Compliance Check</DialogTitle>
        <DialogContent>
          <PPSRCheck
            registration={quickToolsRego}
            state={quickToolsState}
            showCost={true}
            onResultUpdate={(result: any) => {
              console.log('PPSR Result:', result);
            }}
          />
        </DialogContent>
      </Dialog>
      
      {/* Valuation Dialog */}
      <Dialog
        open={valuationDialogOpen}
        onClose={() => {
          setValuationDialogOpen(false);
          setValuationResult(null);
        }}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Vehicle Valuation</DialogTitle>
        <DialogContent>
          {valuationLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress />
            </Box>
          ) : valuationResult ? (
            <Box>
              <Alert severity="success" sx={{ mb: 2 }}>
                Valuation retrieved successfully for {quickToolsRego}
              </Alert>
              <Paper sx={{ p: 3, bgcolor: 'background.default' }}>
                <Typography variant="h6" gutterBottom>
                  Market Valuation
                </Typography>
                <Grid container spacing={2}>
                  <Grid item xs={6}>
                    <Typography variant="body2" color="text.secondary">
                      Retail Price
                    </Typography>
                    <Typography variant="h5" color="primary">
                      ${valuationResult.retail_price?.toLocaleString() || 'N/A'}
                    </Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="body2" color="text.secondary">
                      Trade Price
                    </Typography>
                    <Typography variant="h5">
                      ${valuationResult.trade_price?.toLocaleString() || 'N/A'}
                    </Typography>
                  </Grid>
                  <Grid item xs={12}>
                    <Typography variant="caption" color="text.secondary">
                      Based on {valuationResult.kms?.toLocaleString() || '50,000'} km • 
                      Confidence: {(valuationResult.score * 100).toFixed(0)}%
                    </Typography>
                  </Grid>
                </Grid>
              </Paper>
            </Box>
          ) : quickToolsError ? (
            <Alert severity="error">{quickToolsError}</Alert>
          ) : (
            <Typography>Click "Get Valuation" to retrieve market value</Typography>
          )}
        </DialogContent>
      </Dialog>

      {/* Add Customer Modal */}
      <AddCustomerModal
        open={addCustomerModalOpen}
        onClose={() => setAddCustomerModalOpen(false)}
        onSuccess={(customerId) => {
          navigate(`/customers/${customerId}`);
        }}
      />

      {/* Rego Expiry Modal */}
      <Dialog
        open={regoExpiryModalOpen}
        onClose={() => setRegoExpiryModalOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <WarningIcon color="error" />
            <Typography variant="h6" sx={{ fontWeight: 600 }}>
              Registration Expiry
            </Typography>
          </Box>
        </DialogTitle>
        <DialogContent>
          {expiringRegoVehicles.length === 0 ? (
            <Alert severity="success" sx={{ mt: 1 }}>
              All vehicle registrations are current. No action required.
            </Alert>
          ) : (
            <Box>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                {expiringRegoVehicles.length} vehicle{expiringRegoVehicles.length !== 1 ? 's' : ''} with registration expiring within 30 days
              </Typography>
              <List>
                {expiringRegoVehicles.map((vehicle, index) => (
                  <Box key={vehicle.id}>
                    <ListItem
                      sx={{
                        cursor: 'pointer',
                        borderRadius: 1,
                        '&:hover': {
                          bgcolor: 'action.hover',
                        }
                      }}
                      onClick={() => {
                        setRegoExpiryModalOpen(false);
                        navigate(`/inventory/${vehicle.id}`);
                      }}
                    >
                      <ListItemAvatar>
                        <Avatar sx={{ bgcolor: 'error.main' }}>
                          <CarIcon />
                        </Avatar>
                      </ListItemAvatar>
                      <ListItemText
                        primary={
                          <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                            {vehicle.registration_number} - {vehicle.year} {vehicle.make} {vehicle.model}
                          </Typography>
                        }
                        secondary={
                          <Box>
                            <Typography variant="body2" color="text.secondary">
                              Expires: {format(new Date(vehicle.registration_expiry), 'dd MMM yyyy')}
                            </Typography>
                            <Chip
                              label={`${getDaysUntil(vehicle.registration_expiry)} days left`}
                              size="small"
                              color={getDaysUntil(vehicle.registration_expiry) <= 7 ? 'error' : 'warning'}
                              sx={{ mt: 0.5 }}
                            />
                          </Box>
                        }
                      />
                    </ListItem>
                    {index < expiringRegoVehicles.length - 1 && <Divider />}
                  </Box>
                ))}
              </List>
            </Box>
          )}
        </DialogContent>
      </Dialog>

      {/* Inspection Expiry Modal */}
      <Dialog
        open={inspectionExpiryModalOpen}
        onClose={() => setInspectionExpiryModalOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <AssessmentIcon color="warning" />
            <Typography variant="h6" sx={{ fontWeight: 600 }}>
              Inspection Reports Expiring
            </Typography>
          </Box>
        </DialogTitle>
        <DialogContent>
          {expiringInspectionVehicles.length === 0 ? (
            <Alert severity="success" sx={{ mt: 1 }}>
              All inspection reports are current. No action required.
            </Alert>
          ) : (
            <Box>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                {expiringInspectionVehicles.length} vehicle{expiringInspectionVehicles.length !== 1 ? 's' : ''} with inspection expiring within 30 days
              </Typography>
              <List>
                {expiringInspectionVehicles.map((vehicle, index) => (
                  <Box key={vehicle.id}>
                    <ListItem
                      sx={{
                        cursor: 'pointer',
                        borderRadius: 1,
                        '&:hover': {
                          bgcolor: 'action.hover',
                        }
                      }}
                      onClick={() => {
                        setInspectionExpiryModalOpen(false);
                        navigate(`/inventory/${vehicle.id}`);
                      }}
                    >
                      <ListItemAvatar>
                        <Avatar sx={{ bgcolor: 'warning.main' }}>
                          <CarIcon />
                        </Avatar>
                      </ListItemAvatar>
                      <ListItemText
                        primary={
                          <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                            {vehicle.registration_number} - {vehicle.year} {vehicle.make} {vehicle.model}
                          </Typography>
                        }
                        secondary={
                          <Box>
                            <Typography variant="body2" color="text.secondary">
                              Expires: {format(new Date(vehicle.inspection_expiry), 'dd MMM yyyy')}
                            </Typography>
                            <Chip
                              label={`${getDaysUntil(vehicle.inspection_expiry)} days left`}
                              size="small"
                              color={getDaysUntil(vehicle.inspection_expiry) <= 7 ? 'error' : 'warning'}
                              sx={{ mt: 0.5 }}
                            />
                          </Box>
                        }
                      />
                    </ListItem>
                    {index < expiringInspectionVehicles.length - 1 && <Divider />}
                  </Box>
                ))}
              </List>
            </Box>
          )}
        </DialogContent>
      </Dialog>

      {/* Registration Transfers Modal */}
      <Dialog
        open={transfersModalOpen}
        onClose={() => setTransfersModalOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <TransferIcon color="error" />
            <Typography variant="h6" sx={{ fontWeight: 600 }}>
              Registration Transfers
            </Typography>
          </Box>
        </DialogTitle>
        <DialogContent>
          {pendingRegistrationTransfers.length === 0 ? (
            <Alert severity="success" sx={{ mt: 1 }}>
              All registration transfers complete. No action required.
            </Alert>
          ) : (
            <Box>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                {pendingRegistrationTransfers.length} vehicle{pendingRegistrationTransfers.length !== 1 ? 's' : ''} sold within last 14 days requiring transfer
              </Typography>
              <List>
                {pendingRegistrationTransfers.map((vehicle, index) => (
                  <Box key={vehicle.id}>
                    <ListItem
                      sx={{
                        cursor: 'pointer',
                        borderRadius: 1,
                        '&:hover': {
                          bgcolor: 'action.hover',
                        }
                      }}
                      onClick={() => {
                        setTransfersModalOpen(false);
                        navigate(`/inventory/${vehicle.id}`);
                      }}
                    >
                      <ListItemAvatar>
                        <Avatar sx={{ bgcolor: 'error.main' }}>
                          <CarIcon />
                        </Avatar>
                      </ListItemAvatar>
                      <ListItemText
                        primary={
                          <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                            {vehicle.registration_number} - {vehicle.year} {vehicle.make} {vehicle.model}
                          </Typography>
                        }
                        secondary={
                          <Box>
                            <Typography variant="body2" color="text.secondary">
                              Sold: {vehicle.sold_date ? format(new Date(vehicle.sold_date), 'dd MMM yyyy') : 'N/A'}
                            </Typography>
                            <Chip
                              label={`${getDaysUntil(vehicle.sold_date) + 14} days left to transfer`}
                              size="small"
                              color="error"
                              sx={{ mt: 0.5 }}
                            />
                          </Box>
                        }
                      />
                    </ListItem>
                    {index < pendingRegistrationTransfers.length - 1 && <Divider />}
                  </Box>
                ))}
              </List>
            </Box>
          )}
        </DialogContent>
      </Dialog>

      {/* Notice of Disposal Modal */}
      <Dialog
        open={disposalModalOpen}
        onClose={() => setDisposalModalOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <DocumentIcon color="warning" />
            <Typography variant="h6" sx={{ fontWeight: 600 }}>
              Notice of Disposal
            </Typography>
          </Box>
        </DialogTitle>
        <DialogContent>
          {pendingDisposalNotices.length === 0 ? (
            <Alert severity="success" sx={{ mt: 1 }}>
              All notices of disposal lodged. No action required.
            </Alert>
          ) : (
            <Box>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                {pendingDisposalNotices.length} sale{pendingDisposalNotices.length !== 1 ? 's' : ''} requiring notice lodgement
              </Typography>
              <List>
                {pendingDisposalNotices.map((sale, index) => (
                  <Box key={sale.id}>
                    <ListItem
                      sx={{
                        cursor: 'pointer',
                        borderRadius: 1,
                        '&:hover': {
                          bgcolor: 'action.hover',
                        }
                      }}
                      onClick={() => {
                        setDisposalModalOpen(false);
                        navigate(`/sales`);
                      }}
                    >
                      <ListItemAvatar>
                        <Avatar sx={{ bgcolor: 'warning.main' }}>
                          <DocumentIcon />
                        </Avatar>
                      </ListItemAvatar>
                      <ListItemText
                        primary={
                          <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                            Sale #{sale.id?.substring(0, 8)}
                          </Typography>
                        }
                        secondary={
                          <Box>
                            <Typography variant="body2" color="text.secondary">
                              Sale Date: {sale.sale_date ? format(new Date(sale.sale_date), 'dd MMM yyyy') : 'N/A'}
                            </Typography>
                            <Chip
                              label="Notice required"
                              size="small"
                              color="warning"
                              sx={{ mt: 0.5 }}
                            />
                          </Box>
                        }
                      />
                    </ListItem>
                    {index < pendingDisposalNotices.length - 1 && <Divider />}
                  </Box>
                ))}
              </List>
            </Box>
          )}
        </DialogContent>
      </Dialog>

      {/* Upcoming Tasks Modal */}
      <Dialog
        open={tasksModalOpen}
        onClose={() => setTasksModalOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <TaskIcon color={taskSummary.overdue > 0 ? 'error' : 'warning'} />
            <Typography variant="h6" sx={{ fontWeight: 600 }}>
              {taskSummary.overdue > 0 ? 'Overdue & Upcoming Tasks' : 'Upcoming Tasks'}
            </Typography>
          </Box>
        </DialogTitle>
        <DialogContent>
          {upcomingTasks.length === 0 && taskSummary.overdue === 0 ? (
            <Alert severity="success" sx={{ mt: 1 }}>
              No tasks due this week. All caught up!
            </Alert>
          ) : (
            <Box>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                {taskSummary.overdue > 0 && `${taskSummary.overdue} overdue • `}
                {upcomingTasks.length} task{upcomingTasks.length !== 1 ? 's' : ''} due this week
              </Typography>
              <List>
                {upcomingTasks.map((task, index) => {
                  const isOverdue = task.due_date && new Date(task.due_date) < new Date();
                  return (
                    <Box key={task.id}>
                      <ListItem
                        sx={{
                          cursor: 'pointer',
                          borderRadius: 1,
                          '&:hover': {
                            bgcolor: 'action.hover',
                          }
                        }}
                        onClick={() => {
                          setTasksModalOpen(false);
                          navigate(`/tasks`);
                        }}
                      >
                        <ListItemAvatar>
                          <Avatar sx={{ bgcolor: isOverdue ? 'error.main' : 'warning.main' }}>
                            <TaskIcon />
                          </Avatar>
                        </ListItemAvatar>
                        <ListItemText
                          primary={
                            <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                              {task.title}
                            </Typography>
                          }
                          secondary={
                            <Box>
                              <Typography variant="body2" color="text.secondary">
                                Due: {task.due_date ? format(new Date(task.due_date), 'dd MMM yyyy') : 'No date'}
                              </Typography>
                              <Chip
                                label={isOverdue ? 'Overdue' : `${getDaysUntil(task.due_date)} days left`}
                                size="small"
                                color={isOverdue ? 'error' : 'warning'}
                                sx={{ mt: 0.5 }}
                              />
                            </Box>
                          }
                        />
                      </ListItem>
                      {index < upcomingTasks.length - 1 && <Divider />}
                    </Box>
                  );
                })}
              </List>
            </Box>
          )}
        </DialogContent>
      </Dialog>

      {/* Vehicles Missing Photos Modal */}
      <Dialog
        open={photosModalOpen}
        onClose={() => setPhotosModalOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <PhotoIcon color="info" />
            <Typography variant="h6" sx={{ fontWeight: 600 }}>
              Vehicles Missing Photos
            </Typography>
          </Box>
        </DialogTitle>
        <DialogContent>
          {vehiclesMissingPhotos.length === 0 ? (
            <Alert severity="success" sx={{ mt: 1 }}>
              All vehicles have photos. Great job!
            </Alert>
          ) : (
            <Box>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                {vehiclesMissingPhotos.length} vehicle{vehiclesMissingPhotos.length !== 1 ? 's' : ''} need photos added
              </Typography>
              <List>
                {vehiclesMissingPhotos.map((vehicle, index) => (
                  <Box key={vehicle.id}>
                    <ListItem
                      sx={{
                        cursor: 'pointer',
                        borderRadius: 1,
                        '&:hover': {
                          bgcolor: 'action.hover',
                        }
                      }}
                      onClick={() => {
                        setPhotosModalOpen(false);
                        navigate(`/inventory/${vehicle.id}`);
                      }}
                    >
                      <ListItemAvatar>
                        <Avatar sx={{ bgcolor: 'info.main' }}>
                          <CarIcon />
                        </Avatar>
                      </ListItemAvatar>
                      <ListItemText
                        primary={
                          <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                            {vehicle.registration_number || 'N/A'} - {vehicle.year} {vehicle.make} {vehicle.model}
                          </Typography>
                        }
                        secondary={
                          <Box>
                            <Typography variant="body2" color="text.secondary">
                              Stock: {vehicle.stock_number || 'N/A'}
                            </Typography>
                            <Chip
                              label="Add photos"
                              size="small"
                              color="info"
                              sx={{ mt: 0.5 }}
                            />
                          </Box>
                        }
                      />
                    </ListItem>
                    {index < vehiclesMissingPhotos.length - 1 && <Divider />}
                  </Box>
                ))}
              </List>
            </Box>
          )}
        </DialogContent>
      </Dialog>
    </Box>
  );
}
